/**
 * Resolve the ONE location this org publishes variant artwork against
 * (BLU-655 B2 per-colour upload mode; corrected by BLU-838 V1 / decision D-35).
 *
 * The commit endpoint routes the storage target + junction check off a
 * `locationId`, so the per-colour grid needs exactly one location to upload
 * against.
 *
 * ## The rule: OWNED FIRST, and never guess between two
 *
 * Candidates are the active, NON-DELEGATED locations the org can reach,
 * **owned floors first** and shared-access rows (reached through a client
 * junction at someone else's floor) after them.
 *
 * Delegated ("shadow") rows are dropped before anything else (BLU-1372, via
 * the shared `excludeDelegatedLocations`, the one detector; BLU-879): a shadow
 * routes all its fulfilment to a 3PL floor, so nothing is ever printed there
 * and artwork published against it lands nowhere useful. Worse, because a
 * shadow is an OWNED row, a client with a shadow + the 3PL floor used to
 * auto-resolve to the shadow.
 *
 * Resolution is then deliberately conservative:
 *
 *   - An explicit operator pick       -> that location.
 *   - The location the operator CHOSE in the top-right location picker,
 *     when it is a candidate (BLU-1522 S6a) -> that location. Only a real
 *     choice counts (`selectedByUser`: picked, or their saved pick restored).
 *     The picker also fills itself with automatic defaults (a station role's
 *     first floor, an admin's "HQ" name match or first floor); those are our
 *     guess, not theirs, so they fall through to the rules below, as do "All
 *     locations" (null) and a location that is not a candidate.
 *   - Exactly one OWNED floor         -> that floor, no question asked.
 *   - Exactly ONE candidate in total  -> that candidate, even a shared floor
 *     (BLU-1372: a 3PL client whose only remaining location is the 3PL's
 *     floor has nothing to choose between; the explainer line names it).
 *   - Anything else                   -> NO target until the operator picks.
 *     That covers two owned floors, and "zero owned, two or more shared": a
 *     shared floor is never chosen on the operator's behalf when there is
 *     another candidate it could be confused with.
 *
 * ### Why this is not the "prefer shared" rule it replaces
 *
 * This hook originally preferred `is_shared === true` rows, on the theory that
 * the 3PL-client case (publish into the owner's public bucket through our own
 * active junction) was the interesting one. At ThreadHeads that inverted
 * preference means every "By colour" upload silently targets **Ningbo** — a
 * floor that never RIPs, is all-customs, and is not where these standards
 * print. Publishing standards artwork to a floor the operator never chose is a
 * tampering surface (T-84-03-07), not a 3PL convenience.
 *
 * If a future ticket needs a shared floor to be reachable, it already is — it
 * is in `candidates` and the picker offers it. What must not come back is a
 * shared floor being *preselected* while it has a rival. Do not "fix the 3PL
 * case" by reordering `candidates` or by auto-taking `candidates[0]` when
 * there are two or more.
 *
 * Kept as the SINGLE resolution point (one `locations.list` read) so every
 * surface that publishes artwork agrees on the target. It feeds two surfaces
 * through one instance on the Product sync page: the Designs tier "By colour"
 * picker + grid, and the Product updates picker + location gate.
 *
 * ### Remembered across pages, never guessed (Upload Studio round trip)
 *
 * The explicit pick lives in the page session (`lib/page-session`, per org,
 * in memory, gone on reload), shared by every instance: a floor picked on
 * Product sync is the studio's floor too, and one picked in the studio holds
 * for the next product of the walk. `preferredId` is the floor the page that
 * opened the studio was publishing to (carried in router state): used only
 * when there is no pick and only when it is still a candidate, so it is the
 * operator's own floor carried over, never a guess.
 */
import { useMemo } from 'react';
import { useOrganization } from '@/contexts/OrganizationContext';
import { useLocation as useLocationContext } from '@/contexts/LocationContext';
import { pageSessionScope, usePageSessionState } from '@/lib/page-session';
import { trpc } from '@/lib/trpc';
import { excludeDelegatedLocations } from '@/features/locations/utils/delegatedLocations';

export interface ArtworkUploadLocation {
  id: string;
  name: string;
  /** True when reached via a 3PL client junction (someone else's warehouse). */
  isShared: boolean;
}

export interface ArtworkUploadTarget {
  /**
   * The resolved upload target, or null when there is nothing safe to resolve:
   * no candidates at all, or an ambiguous choice the operator has not made yet.
   * `null` NEVER means "fall back to the first candidate".
   */
  location: ArtworkUploadLocation | null;
  /** Every active location the org can publish against — owned first. */
  candidates: ArtworkUploadLocation[];
  /** The operator's explicit pick, or null when they have not chosen. */
  selectedId: string | null;
  /** Record an explicit pick. */
  select: (id: string) => void;
  /**
   * `locations.list` is still in flight. Consumers MUST render a skeleton on
   * this rather than the "no active print location" empty state — an empty
   * state during a pending read reads as "you have no floors", which is a lie.
   */
  isLoading: boolean;
  /**
   * Candidates exist but none is resolved: the operator has to pick before any
   * upload affordance is safe to enable.
   */
  needsChoice: boolean;
}

/** The page session scope of the remembered pick (not a page: no visit ever drops it). */
const PICK_SESSION = 'artwork-upload-location';

export function useArtworkUploadLocation(options: { preferredId?: string | null } = {}): ArtworkUploadTarget {
  const { preferredId = null } = options;
  const { organization } = useOrganization();
  // The top-right location picker, only when the operator chose it: its
  // automatic default (e.g. a shared floor named "HQ") is never a pick.
  const { selectedLocation: topRight, selectedByUser } = useLocationContext();
  const topRightId = selectedByUser ? (topRight?.id ?? null) : null;
  const query = trpc.locations.list.useQuery(
    { organizationId: organization?.id },
    { enabled: !!organization?.id, staleTime: 60_000 },
  );
  const pickScope = useMemo(() => pageSessionScope(organization?.id, PICK_SESSION), [organization?.id]);
  const [selectedId, select] = usePageSessionState<string | null>(pickScope, 'picked', null);

  const candidates = useMemo<ArtworkUploadLocation[]>(() => {
    // Shadow rows first (BLU-1372): never a publish target, never a candidate.
    const rows = excludeDelegatedLocations(query.data ?? []);
    // `list` already returns active rows by default (includeInactive unset), but
    // guard against a false is_active defensively.
    const active = rows.filter((l) => l.is_active !== false);
    const owned = active.filter((l) => l.is_shared !== true);
    const shared = active.filter((l) => l.is_shared === true);
    // Owned first — this ordering is the rule, not a cosmetic sort.
    return [...owned, ...shared].map((l) => ({
      id: l.id,
      name: l.name,
      isShared: l.is_shared === true,
    }));
  }, [query.data]);

  const location = useMemo<ArtworkUploadLocation | null>(() => {
    const find = (id: string | null) => (id ? candidates.find((c) => c.id === id) : undefined);
    const picked = find(selectedId) ?? find(topRightId);
    if (picked) return picked;
    // The floor the opening page was publishing to, while still a candidate.
    const preferred = preferredId ? candidates.find((c) => c.id === preferredId) : undefined;
    if (preferred) return preferred;
    const owned = candidates.filter((c) => !c.isShared);
    // A single owned floor wins over any number of shared ones.
    if (owned.length === 1) return owned[0] ?? null;
    // A lone candidate (even a shared floor) has no rival to be confused with.
    if (candidates.length === 1) return candidates[0] ?? null;
    // Two or more, none owned-unique: the operator picks.
    return null;
  }, [candidates, selectedId, topRightId, preferredId]);

  return {
    location,
    candidates,
    selectedId,
    select,
    isLoading: query.isLoading,
    needsChoice: !location && candidates.length > 0,
  };
}
